import React, { useState, useEffect } from 'react';
import {
  Building2, Users, FileText, CheckCircle2, Clock, Search,
  RefreshCw, Copy, Check, ExternalLink, Sliders, AlertCircle,
  Eye, Lock, Globe, Briefcase, Scale, Calculator, UserCheck,
  FileCheck, Sparkles, Bot, Phone, Mail, MapPin, CheckSquare,
  LogOut, Plus, ChevronRight, X, Filter, Trash2, Edit3,
  Download, ArrowRight, ShieldCheck, MessageSquare, Volume2,
  Calendar, Layers, UserPlus, AlertTriangle, Printer
} from 'lucide-react';
import { LunaFace, LUNA_FACE_CATALOG } from '../LunaFace';

interface CorporateAdminDashboardProps {
  token: string;
  onLogout: () => void;
}

const ALL_SERVICES = [
  { id: 'tax_filling', label: 'Business Tax & Return Filing', icon: Calculator, color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30' },
  { id: 'secretarial', label: 'Company Secretarial Services', icon: Briefcase, color: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/30' },
  { id: 'consulting', label: 'Business Consulting & Strategy', icon: Globe, color: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/30' },
  { id: 'hrm', label: 'HRM & Payroll Services', icon: Users, color: 'text-pink-400', bg: 'bg-pink-500/10', border: 'border-pink-500/30' },
  { id: 'accounting', label: 'Accounting & Financials', icon: FileText, color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/30' },
  { id: 'auditing_internal', label: 'Internal Auditing', icon: ShieldCheck, color: 'text-indigo-400', bg: 'bg-indigo-500/10', border: 'border-indigo-500/30' },
  { id: 'auditing_external', label: 'External Auditing', icon: FileCheck, color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/30' },
  { id: 'legal', label: 'Legal & Contract Services', icon: Scale, color: 'text-rose-400', bg: 'bg-rose-500/10', border: 'border-rose-500/30' },
  { id: 'other', label: 'Other Corporate Advisory', icon: Sparkles, color: 'text-teal-400', bg: 'bg-teal-500/10', border: 'border-teal-500/30' },
];

export default function CorporateAdminDashboard({ token, onLogout }: CorporateAdminDashboardProps) {
  const [activeTab, setActiveTab] = useState<'command_center' | 'clients' | 'filings' | 'services' | 'luna_studio' | 'branding'>('command_center');
  const [firm, setFirm] = useState<any>(null);
  const [stats, setStats] = useState<any>({ totalClients: 0, totalFilings: 0, pendingFilings: 0, approvedFilings: 0 });
  const [loading, setLoading] = useState(true);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Clients State
  const [clients, setClients] = useState<any[]>([]);
  const [clientSearch, setClientSearch] = useState('');
  const [isAddClientModalOpen, setIsAddClientModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<any | null>(null);
  const [newClient, setNewClient] = useState({
    fullName: '',
    email: '',
    password: '',
    phone: '',
    businessName: '',
    tinNumber: '',
    assignedServices: ALL_SERVICES.map(s => s.id),
    notes: ''
  });
  const [savingClient, setSavingClient] = useState(false);

  // Filings State
  const [filings, setFilings] = useState<any[]>([]);
  const [filingFilterService, setFilingFilterService] = useState('all');
  const [filingFilterStatus, setFilingFilterStatus] = useState('all');
  const [filingSearch, setFilingSearch] = useState('');
  const [selectedFiling, setSelectedFiling] = useState<any | null>(null);
  const [reviewerNotes, setReviewerNotes] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Luna & Branding Settings State
  const [lunaSettings, setLunaSettings] = useState({
    faceId: 'holo_android',
    themeColor: '#3b82f6',
    voiceEnabled: true,
    language: 'en',
    tone: 'professional',
    customInstructions: ''
  });
  const [brandingSettings, setBrandingSettings] = useState({
    name: '',
    phone: '',
    address: '',
    logoUrl: '',
    customTitle: '',
    themeColor: '#3b82f6'
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [testSpeaking, setTestSpeaking] = useState(false);

  const authHeader = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  });

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 4000);
  };

  // Fetch Firm Me & Stats
  const fetchFirmData = async () => {
    try {
      const res = await fetch('/api/corporate/admin/me', { headers: authHeader() });
      const data = await res.json();
      if (data.success) {
        setFirm(data.firm);
        setStats(data.stats);
        if (data.firm.branding) {
          setBrandingSettings({
            name: data.firm.name || '',
            phone: data.firm.phone || '',
            address: data.firm.address || '',
            logoUrl: data.firm.branding.logoUrl || '',
            customTitle: data.firm.branding.customTitle || '',
            themeColor: data.firm.branding.themeColor || '#3b82f6'
          });
        }
        if (data.firm.lunaConfig) {
          setLunaSettings({
            faceId: data.firm.lunaConfig.faceId || 'holo_android',
            themeColor: data.firm.lunaConfig.themeColor || '#3b82f6',
            voiceEnabled: data.firm.lunaConfig.voiceEnabled !== false,
            language: data.firm.lunaConfig.language || 'en',
            tone: data.firm.lunaConfig.tone || 'professional',
            customInstructions: data.firm.lunaConfig.customInstructions || ''
          });
        }
      } else {
        showToast(data.error || 'Failed to load firm profile');
      }
    } catch (err: any) {
      console.error('Error fetching firm data:', err);
    }
  };

  // Fetch Clients
  const fetchClients = async () => {
    try {
      const res = await fetch('/api/corporate/admin/clients', { headers: authHeader() });
      const data = await res.json();
      if (data.success) {
        setClients(data.clients || []);
      }
    } catch (err) {
      console.error('Error loading clients:', err);
    }
  };

  // Fetch Filings
  const fetchFilings = async () => {
    try {
      const res = await fetch('/api/corporate/admin/filings', { headers: authHeader() });
      const data = await res.json();
      if (data.success) {
        setFilings(data.filings || []);
      }
    } catch (err) {
      console.error('Error loading filings:', err);
    }
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchFirmData(), fetchClients(), fetchFilings()]);
      setLoading(false);
    };
    init();
  }, [token]);

  // Handle Add Client
  const handleAddClient = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingClient(true);
    try {
      const res = await fetch('/api/corporate/admin/clients', {
        method: 'POST',
        headers: authHeader(),
        body: JSON.stringify(newClient)
      });
      const data = await res.json();
      if (data.success) {
        showToast(`Client account "${newClient.fullName}" created successfully!`);
        setIsAddClientModalOpen(false);
        setNewClient({
          fullName: '',
          email: '',
          password: '',
          phone: '',
          businessName: '',
          tinNumber: '',
          assignedServices: ALL_SERVICES.map(s => s.id),
          notes: ''
        });
        fetchClients();
        fetchFirmData();
      } else {
        showToast(data.error || 'Failed to create client');
      }
    } catch (err: any) {
      showToast(err?.message || 'Error creating client');
    } finally {
      setSavingClient(false);
    }
  };

  // Handle Filing Status Update
  const handleUpdateFilingStatus = async (filingId: number, newStatus: string) => {
    setUpdatingStatus(true);
    try {
      const res = await fetch(`/api/corporate/admin/filings/${filingId}/status`, {
        method: 'PUT',
        headers: authHeader(),
        body: JSON.stringify({ status: newStatus, reviewerNotes })
      });
      const data = await res.json();
      if (data.success) {
        showToast(`Filing status updated to ${newStatus.toUpperCase()}!`);
        setSelectedFiling(data.filing);
        fetchFilings();
        fetchFirmData();
      } else {
        showToast(data.error || 'Failed to update filing status');
      }
    } catch (err: any) {
      showToast(err?.message || 'Error updating status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Save Settings
  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      const res = await fetch('/api/corporate/admin/settings', {
        method: 'PUT',
        headers: authHeader(),
        body: JSON.stringify({
          name: brandingSettings.name,
          phone: brandingSettings.phone,
          address: brandingSettings.address,
          branding: {
            logoUrl: brandingSettings.logoUrl,
            customTitle: brandingSettings.customTitle,
            themeColor: brandingSettings.themeColor
          },
          lunaConfig: lunaSettings
        })
      });
      const data = await res.json();
      if (data.success) {
        showToast('Settings saved successfully!');
        setFirm(data.firm);
      } else {
        showToast(data.error || 'Failed to save settings');
      }
    } catch (err: any) {
      showToast(err?.message || 'Error saving settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const filteredClients = clients.filter(c => {
    const q = clientSearch.toLowerCase();
    return c.fullName?.toLowerCase().includes(q) ||
           c.email?.toLowerCase().includes(q) ||
           c.businessName?.toLowerCase().includes(q) ||
           c.tinNumber?.toLowerCase().includes(q);
  });

  const filteredFilings = filings.filter(f => {
    const matchesService = filingFilterService === 'all' || f.serviceType === filingFilterService;
    const matchesStatus = filingFilterStatus === 'all' || f.status === filingFilterStatus;
    const q = filingSearch.toLowerCase();
    const matchesSearch = !q ||
      f.formTitle?.toLowerCase().includes(q) ||
      f.client?.fullName?.toLowerCase().includes(q) ||
      f.client?.businessName?.toLowerCase().includes(q) ||
      f.client?.tinNumber?.toLowerCase().includes(q);
    return matchesService && matchesStatus && matchesSearch;
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
      case 'filed':
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 flex items-center gap-1"><CheckCircle2 size={12} /> {status.toUpperCase()}</span>;
      case 'in_review':
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-amber-500/20 text-amber-300 border border-amber-500/30 flex items-center gap-1"><Clock size={12} /> IN REVIEW</span>;
      case 'rejected':
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-rose-500/20 text-rose-300 border border-rose-500/30 flex items-center gap-1"><AlertTriangle size={12} /> REJECTED</span>;
      default:
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-slate-500/20 text-slate-300 border border-slate-500/30 flex items-center gap-1"><Clock size={12} /> DRAFT</span>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex flex-col items-center justify-center gap-4">
        <RefreshCw className="w-8 h-8 text-blue-400 animate-spin" />
        <p className="text-sm font-mono text-slate-400">Authenticating Corporate Advisory Workspace...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030712] text-slate-100 flex flex-col font-sans selection:bg-blue-500 selection:text-white">
      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed top-6 right-6 z-50 bg-blue-600 text-white px-5 py-3 rounded-2xl shadow-2xl border border-blue-400/40 flex items-center gap-3 animate-fade-in text-sm font-bold">
          <Sparkles className="w-4 h-4 text-blue-200" />
          {toastMessage}
        </div>
      )}

      {/* Top Navbar */}
      <header className="sticky top-0 z-40 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800/80 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-tr from-blue-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/20 border border-blue-400/30">
            <Building2 className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-base font-black tracking-tight text-white">{firm?.name || 'Corporate Firm Suite'}</h1>
              <span className="px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-wider bg-blue-500/20 text-blue-300 border border-blue-500/30">FIRM ADMIN</span>
            </div>
            <p className="text-xs text-slate-400 font-mono">ID: {firm?.apiKey?.substring(0, 14)}••• | Tenet Luna Powered</p>
          </div>
        </div>

        {/* Global Quick Tabs */}
        <div className="hidden lg:flex items-center gap-1 bg-slate-900/90 p-1.5 rounded-2xl border border-slate-800">
          <button
            onClick={() => setActiveTab('command_center')}
            className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 ${
              activeTab === 'command_center' ? 'bg-blue-600 text-white shadow-md shadow-blue-600/30' : 'text-slate-400 hover:text-white'
            }`}
          >
            <Layers size={14} /> Command Center
          </button>
          <button
            onClick={() => setActiveTab('clients')}
            className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 ${
              activeTab === 'clients' ? 'bg-blue-600 text-white shadow-md shadow-blue-600/30' : 'text-slate-400 hover:text-white'
            }`}
          >
            <Users size={14} /> Clients ({stats.totalClients})
          </button>
          <button
            onClick={() => setActiveTab('filings')}
            className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 ${
              activeTab === 'filings' ? 'bg-blue-600 text-white shadow-md shadow-blue-600/30' : 'text-slate-400 hover:text-white'
            }`}
          >
            <FileText size={14} /> Filings Review
            {stats.pendingFilings > 0 && (
              <span className="w-5 h-5 rounded-full bg-amber-500 text-slate-950 font-black text-[10px] flex items-center justify-center animate-pulse">
                {stats.pendingFilings}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('services')}
            className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 ${
              activeTab === 'services' ? 'bg-blue-600 text-white shadow-md shadow-blue-600/30' : 'text-slate-400 hover:text-white'
            }`}
          >
            <Briefcase size={14} /> Services
          </button>
          <button
            onClick={() => setActiveTab('luna_studio')}
            className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 ${
              activeTab === 'luna_studio' ? 'bg-blue-600 text-white shadow-md shadow-blue-600/30' : 'text-slate-400 hover:text-white'
            }`}
          >
            <Bot size={14} /> Luna AI Studio
          </button>
          <button
            onClick={() => setActiveTab('branding')}
            className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 ${
              activeTab === 'branding' ? 'bg-blue-600 text-white shadow-md shadow-blue-600/30' : 'text-slate-400 hover:text-white'
            }`}
          >
            <Sliders size={14} /> Settings
          </button>
        </div>

        {/* Right Action Buttons */}
        <div className="flex items-center gap-3">
          <a
            href="/corporate"
            target="_blank"
            rel="noreferrer"
            className="hidden sm:flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-bold bg-slate-900 border border-slate-800 text-slate-300 hover:text-white hover:border-slate-700 transition-all"
          >
            <ExternalLink size={13} /> Client Portal View
          </a>
          <button
            onClick={onLogout}
            className="flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-bold bg-rose-500/10 text-rose-400 border border-rose-500/20 hover:bg-rose-500/20 transition-all"
          >
            <LogOut size={13} /> Sign Out
          </button>
        </div>
      </header>

      {/* Main Container */}
      <main className="flex-1 p-6 max-w-7xl mx-auto w-full space-y-6">

        {/* ════════════════════════════════════════════════════════════════════════
            TAB 1: COMMAND CENTER
        ════════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'command_center' && (
          <div className="space-y-6 animate-fade-in">
            {/* Top Stat Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-slate-900/60 backdrop-blur-md border border-slate-800/80 p-5 rounded-3xl relative overflow-hidden group hover:border-blue-500/40 transition-all">
                <div className="absolute top-0 right-0 w-24 h-24 bg-blue-500/10 rounded-full blur-2xl group-hover:bg-blue-500/20 transition-all" />
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Active Clients</span>
                  <div className="w-9 h-9 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400">
                    <Users size={18} />
                  </div>
                </div>
                <div className="text-3xl font-black text-white">{stats.totalClients}</div>
                <p className="text-xs text-slate-400 mt-1">Enrolled corporate client accounts</p>
              </div>

              <div className="bg-slate-900/60 backdrop-blur-md border border-slate-800/80 p-5 rounded-3xl relative overflow-hidden group hover:border-amber-500/40 transition-all">
                <div className="absolute top-0 right-0 w-24 h-24 bg-amber-500/10 rounded-full blur-2xl group-hover:bg-amber-500/20 transition-all" />
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Review Queue</span>
                  <div className="w-9 h-9 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400">
                    <Clock size={18} />
                  </div>
                </div>
                <div className="text-3xl font-black text-amber-400">{stats.pendingFilings}</div>
                <p className="text-xs text-slate-400 mt-1">Filings awaiting firm manager sign-off</p>
              </div>

              <div className="bg-slate-900/60 backdrop-blur-md border border-slate-800/80 p-5 rounded-3xl relative overflow-hidden group hover:border-emerald-500/40 transition-all">
                <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-500/10 rounded-full blur-2xl group-hover:bg-emerald-500/20 transition-all" />
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Approved & Filed</span>
                  <div className="w-9 h-9 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400">
                    <CheckCircle2 size={18} />
                  </div>
                </div>
                <div className="text-3xl font-black text-emerald-400">{stats.approvedFilings}</div>
                <p className="text-xs text-slate-400 mt-1">Successfully completed submissions</p>
              </div>

              <div className="bg-slate-900/60 backdrop-blur-md border border-slate-800/80 p-5 rounded-3xl relative overflow-hidden group hover:border-purple-500/40 transition-all">
                <div className="absolute top-0 right-0 w-24 h-24 bg-purple-500/10 rounded-full blur-2xl group-hover:bg-purple-500/20 transition-all" />
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Total Filings</span>
                  <div className="w-9 h-9 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400">
                    <FileText size={18} />
                  </div>
                </div>
                <div className="text-3xl font-black text-purple-300">{stats.totalFilings}</div>
                <p className="text-xs text-slate-400 mt-1">Cumulative filings processed</p>
              </div>
            </div>

            {/* Quick Actions & Welcome Banner */}
            <div className="bg-gradient-to-r from-blue-900/40 via-indigo-900/30 to-purple-900/40 border border-blue-500/30 rounded-3xl p-6 sm:p-8 flex flex-col md:flex-row items-center justify-between gap-6 shadow-2xl relative overflow-hidden">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-blue-400" />
                  <span className="text-xs font-bold uppercase tracking-widest text-blue-300">Autonomous Advisory Hub</span>
                </div>
                <h2 className="text-2xl font-black text-white">Empower Your Clients with Tenet Luna AI</h2>
                <p className="text-sm text-slate-300 max-w-xl">
                  Create client user accounts so your customers can complete Tax Returns, Company Secretarial Form 13/20, EPF/ETF, and Auditing dockets 100% hands-free using voice autopilot.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={() => { setIsAddClientModalOpen(true); }}
                  className="px-5 py-3 rounded-2xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs flex items-center gap-2 shadow-lg shadow-blue-600/30 transition-all"
                >
                  <UserPlus size={16} /> Create Client Account
                </button>
                <button
                  onClick={() => setActiveTab('filings')}
                  className="px-5 py-3 rounded-2xl bg-slate-900 hover:bg-slate-800 text-slate-200 border border-slate-700 font-bold text-xs flex items-center gap-2 transition-all"
                >
                  <FileCheck size={16} /> Open Review Queue
                </button>
              </div>
            </div>

            {/* Recent Submissions Table Preview */}
            <div className="bg-slate-900/60 backdrop-blur-md border border-slate-800 rounded-3xl p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-base font-bold text-white">Recent Client Submissions</h3>
                  <p className="text-xs text-slate-400">Latest statutory forms and filing requests from your clients</p>
                </div>
                <button onClick={() => setActiveTab('filings')} className="text-xs text-blue-400 hover:underline font-bold flex items-center gap-1">
                  View All Submissions <ChevronRight size={14} />
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="border-b border-slate-800 text-slate-400 uppercase tracking-wider text-[10px]">
                      <th className="py-3 px-4">Client / Business</th>
                      <th className="py-3 px-4">Service Category</th>
                      <th className="py-3 px-4">Form Title</th>
                      <th className="py-3 px-4">Status</th>
                      <th className="py-3 px-4">Submitted</th>
                      <th className="py-3 px-4 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/60">
                    {filings.slice(0, 5).map((f) => (
                      <tr key={f.id} className="hover:bg-white/[0.02] transition-colors">
                        <td className="py-3 px-4">
                          <div className="font-bold text-white">{f.client?.fullName || 'Client User'}</div>
                          <div className="text-[11px] text-slate-400 font-mono">{f.client?.businessName || f.client?.email}</div>
                        </td>
                        <td className="py-3 px-4">
                          <span className="capitalize font-mono text-slate-300">{f.serviceType?.replace('_', ' ')}</span>
                        </td>
                        <td className="py-3 px-4 font-bold text-slate-200">{f.formTitle}</td>
                        <td className="py-3 px-4">{getStatusBadge(f.status)}</td>
                        <td className="py-3 px-4 text-slate-400 font-mono">
                          {f.updatedAt ? new Date(f.updatedAt).toLocaleDateString() : 'Recent'}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <button
                            onClick={() => { setSelectedFiling(f); setActiveTab('filings'); }}
                            className="px-3 py-1.5 rounded-lg bg-blue-500/10 text-blue-400 border border-blue-500/20 hover:bg-blue-500/20 font-bold text-xs"
                          >
                            Review
                          </button>
                        </td>
                      </tr>
                    ))}
                    {filings.length === 0 && (
                      <tr>
                        <td colSpan={6} className="text-center py-8 text-slate-500">
                          No submissions recorded yet. Create client user accounts to start receiving filings.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════════
            TAB 2: CLIENT USER MANAGEMENT
        ════════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'clients' && (
          <div className="space-y-6 animate-fade-in">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-white">Client User Directory</h2>
                <p className="text-xs text-slate-400">Manage client accounts who can access your corporate filing portal</p>
              </div>
              <button
                onClick={() => setIsAddClientModalOpen(true)}
                className="px-5 py-2.5 rounded-2xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs flex items-center gap-2 shadow-lg shadow-blue-600/30 transition-all"
              >
                <UserPlus size={16} /> Add New Client Account
              </button>
            </div>

            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                type="text"
                placeholder="Search by client name, email, company or TIN..."
                value={clientSearch}
                onChange={(e) => setClientSearch(e.target.value)}
                className="w-full bg-slate-900/80 border border-slate-800 rounded-2xl pl-11 pr-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-all"
              />
            </div>

            {/* Client Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredClients.map((client) => (
                <div
                  key={client.id}
                  className="bg-slate-900/60 backdrop-blur-md border border-slate-800 rounded-3xl p-5 space-y-4 hover:border-slate-700 transition-all flex flex-col justify-between"
                >
                  <div className="space-y-3">
                    <div className="flex items-start justify-between">
                      <div className="w-10 h-10 rounded-2xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400 font-black text-sm">
                        {client.fullName?.charAt(0) || 'U'}
                      </div>
                      <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                        client.status === 'active' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-rose-500/10 text-rose-400 border border-rose-500/20'
                      }`}>
                        {client.status}
                      </span>
                    </div>

                    <div>
                      <h3 className="text-base font-bold text-white">{client.fullName}</h3>
                      <p className="text-xs text-slate-400 font-mono">{client.email}</p>
                    </div>

                    <div className="bg-slate-950/60 p-3 rounded-2xl border border-slate-800/60 space-y-1.5 text-xs">
                      {client.businessName && (
                        <div className="flex items-center justify-between text-slate-300">
                          <span className="text-slate-500">Business:</span>
                          <span className="font-bold">{client.businessName}</span>
                        </div>
                      )}
                      {client.tinNumber && (
                        <div className="flex items-center justify-between text-slate-300">
                          <span className="text-slate-500">TIN Number:</span>
                          <span className="font-mono font-bold text-cyan-400">{client.tinNumber}</span>
                        </div>
                      )}
                      {client.phone && (
                        <div className="flex items-center justify-between text-slate-300">
                          <span className="text-slate-500">Phone:</span>
                          <span className="font-mono">{client.phone}</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between text-slate-300 pt-1 border-t border-slate-800/60">
                        <span className="text-slate-500">Filings Count:</span>
                        <span className="font-bold text-blue-400">{(client.filings || []).length} submissions</span>
                      </div>
                    </div>
                  </div>

                  <div className="pt-2 border-t border-slate-800/60 flex items-center justify-between gap-2">
                    <button
                      onClick={() => {
                        setSelectedClient(client);
                        setFilingSearch(client.email);
                        setActiveTab('filings');
                      }}
                      className="flex-1 py-2 rounded-xl bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 font-bold text-xs text-center transition-all border border-blue-500/20"
                    >
                      View Filings
                    </button>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(`Email: ${client.email}\nPortal URL: ${window.location.origin}/corporate`);
                        showToast('Client login details copied to clipboard!');
                      }}
                      className="px-3 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold transition-all"
                      title="Copy login details"
                    >
                      <Copy size={13} />
                    </button>
                  </div>
                </div>
              ))}

              {filteredClients.length === 0 && (
                <div className="col-span-full text-center py-12 bg-slate-900/30 rounded-3xl border border-slate-800">
                  <Users className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <h3 className="text-base font-bold text-white">No Client Accounts Found</h3>
                  <p className="text-xs text-slate-400 mt-1">Create your first client account so they can start submitting forms.</p>
                  <button
                    onClick={() => setIsAddClientModalOpen(true)}
                    className="mt-4 px-5 py-2.5 rounded-2xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs inline-flex items-center gap-2"
                  >
                    <UserPlus size={16} /> Create Client Account
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════════
            TAB 3: FILINGS & SUBMISSIONS REVIEW PIPELINE
        ════════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'filings' && (
          <div className="space-y-6 animate-fade-in">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-white">Filings & Statutory Submissions</h2>
                <p className="text-xs text-slate-400">Review, audit, approve, and finalize client tax and corporate filings</p>
              </div>
              <button
                onClick={fetchFilings}
                className="px-4 py-2 rounded-xl bg-slate-900 border border-slate-800 text-slate-300 hover:text-white font-bold text-xs flex items-center gap-2"
              >
                <RefreshCw size={13} /> Refresh Queue
              </button>
            </div>

            {/* Filter Bar */}
            <div className="bg-slate-900/80 border border-slate-800 p-4 rounded-3xl flex flex-wrap items-center justify-between gap-4">
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <Filter size={14} /> Service:
                </div>
                <select
                  value={filingFilterService}
                  onChange={(e) => setFilingFilterService(e.target.value)}
                  className="bg-slate-950 border border-slate-800 rounded-xl px-3 py-1.5 text-xs text-white outline-none focus:border-blue-500"
                >
                  <option value="all">All Services</option>
                  {ALL_SERVICES.map(s => (
                    <option key={s.id} value={s.id}>{s.label}</option>
                  ))}
                </select>

                <div className="flex items-center gap-2 text-xs text-slate-400 ml-2">
                  Status:
                </div>
                <select
                  value={filingFilterStatus}
                  onChange={(e) => setFilingFilterStatus(e.target.value)}
                  className="bg-slate-950 border border-slate-800 rounded-xl px-3 py-1.5 text-xs text-white outline-none focus:border-blue-500"
                >
                  <option value="all">All Statuses</option>
                  <option value="in_review">In Review</option>
                  <option value="approved">Approved</option>
                  <option value="filed">Filed</option>
                  <option value="draft">Draft</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>

              <div className="w-full sm:w-64">
                <input
                  type="text"
                  placeholder="Search filings..."
                  value={filingSearch}
                  onChange={(e) => setFilingSearch(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-1.5 text-xs text-white outline-none focus:border-blue-500"
                />
              </div>
            </div>

            {/* Filings List */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredFilings.map((filing) => (
                <div
                  key={filing.id}
                  onClick={() => { setSelectedFiling(filing); setReviewerNotes(filing.reviewerNotes || ''); }}
                  className={`bg-slate-900/60 backdrop-blur-md border rounded-3xl p-5 space-y-4 cursor-pointer hover:border-blue-500/50 transition-all ${
                    selectedFiling?.id === filing.id ? 'border-blue-500 bg-blue-950/20' : 'border-slate-800'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <span className="text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-lg bg-slate-800 text-slate-300 font-mono">
                      {filing.serviceType?.replace('_', ' ')}
                    </span>
                    {getStatusBadge(filing.status)}
                  </div>

                  <div>
                    <h3 className="text-base font-bold text-white line-clamp-1">{filing.formTitle}</h3>
                    <p className="text-xs text-slate-400 font-mono">
                      Client: <span className="text-white font-bold">{filing.client?.fullName}</span>
                    </p>
                    {filing.client?.businessName && (
                      <p className="text-xs text-slate-500 font-mono">Biz: {filing.client?.businessName}</p>
                    )}
                  </div>

                  <div className="pt-3 border-t border-slate-800/60 flex items-center justify-between text-xs text-slate-400">
                    <span>Docs: {(filing.extractedDocuments || []).length} attached</span>
                    <span className="font-mono">{new Date(filing.updatedAt).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}

              {filteredFilings.length === 0 && (
                <div className="col-span-full text-center py-12 bg-slate-900/30 rounded-3xl border border-slate-800">
                  <FileText className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <h3 className="text-base font-bold text-white">No Filings Found</h3>
                  <p className="text-xs text-slate-400 mt-1">No submissions match the selected filters.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════════
            TAB 4: SERVICES CATALOG
        ════════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'services' && (
          <div className="space-y-6 animate-fade-in">
            <div>
              <h2 className="text-xl font-bold text-white">Corporate Services Catalog</h2>
              <p className="text-xs text-slate-400">Enabled advisory and filing services for your firm's clients</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {ALL_SERVICES.map((s) => {
                const Icon = s.icon;
                const isEnabled = (firm?.servicesEnabled || []).includes(s.id);
                return (
                  <div
                    key={s.id}
                    className={`p-6 rounded-3xl border transition-all flex flex-col justify-between gap-4 ${
                      isEnabled ? 'bg-slate-900/80 border-slate-800' : 'bg-slate-950/40 border-slate-900 opacity-60'
                    }`}
                  >
                    <div className="space-y-3">
                      <div className={`w-12 h-12 rounded-2xl ${s.bg} ${s.border} border flex items-center justify-center ${s.color}`}>
                        <Icon size={24} />
                      </div>
                      <div>
                        <h3 className="text-base font-bold text-white">{s.label}</h3>
                        <p className="text-xs text-slate-400 mt-1">
                          Full voice autopilot and document extraction enabled for this corporate discipline.
                        </p>
                      </div>
                    </div>

                    <div className="pt-4 border-t border-slate-800/60 flex items-center justify-between">
                      <span className={`text-xs font-bold ${isEnabled ? 'text-emerald-400' : 'text-slate-500'}`}>
                        {isEnabled ? '● Active in Portal' : '○ Disabled'}
                      </span>
                      <span className="text-[10px] font-mono text-slate-500 uppercase">Tenet Luna Ready</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════════
            TAB 5: LUNA AI VOICE STUDIO
        ════════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'luna_studio' && (
          <div className="space-y-6 animate-fade-in">
            <div>
              <h2 className="text-xl font-bold text-white">Tenet Luna AI Corporate Studio</h2>
              <p className="text-xs text-slate-400">Configure your firm's chartered AI consultant persona, tone, avatar, and statutory knowledge</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Left Column: Avatar & Live Preview */}
              <div className="lg:col-span-5 space-y-6">
                <div className="bg-slate-900/60 backdrop-blur-md border border-slate-800 rounded-3xl p-6 flex flex-col items-center text-center relative overflow-hidden">
                  <div className="w-48 h-48 my-2">
                    <LunaFace
                      faceId={lunaSettings.faceId}
                      themeColor={lunaSettings.themeColor}
                      isSpeaking={testSpeaking}
                      isListening={false}
                    />
                  </div>
                  <h3 className="text-lg font-black text-white mt-2">Luna Corporate Co-Pilot</h3>
                  <p className="text-xs text-slate-400 font-mono">Avatar: {lunaSettings.faceId}</p>

                  <button
                    onClick={() => {
                      setTestSpeaking(true);
                      setTimeout(() => setTestSpeaking(false), 3000);
                    }}
                    className="mt-4 px-4 py-2 rounded-xl bg-blue-600/20 text-blue-300 border border-blue-500/30 hover:bg-blue-600/30 text-xs font-bold flex items-center gap-2 transition-all"
                  >
                    <Volume2 size={14} /> Test Speaking Animation
                  </button>
                </div>

                {/* Avatar Selection Grid */}
                <div className="bg-slate-900/60 backdrop-blur-md border border-slate-800 rounded-3xl p-6 space-y-3">
                  <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider">Select Avatar Model</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {LUNA_FACE_CATALOG.map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => setLunaSettings({ ...lunaSettings, faceId: cat.id })}
                        className={`p-3 rounded-2xl border text-left transition-all ${
                          lunaSettings.faceId === cat.id ? 'bg-blue-600/20 border-blue-500 text-white' : 'bg-slate-950/60 border-slate-800/80 text-slate-400 hover:text-white'
                        }`}
                      >
                        <div className="text-xs font-bold">{cat.name}</div>
                        <div className="text-[10px] text-slate-500 font-mono">{cat.badge}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right Column: AI Persona & Statutory Guidelines */}
              <div className="lg:col-span-7 bg-slate-900/60 backdrop-blur-md border border-slate-800 rounded-3xl p-6 space-y-6">
                <div className="space-y-4">
                  <h4 className="text-sm font-bold text-white">Consultant Persona & Voice Rules</h4>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-400 mb-1">AI Tone</label>
                      <select
                        value={lunaSettings.tone}
                        onChange={(e) => setLunaSettings({ ...lunaSettings, tone: e.target.value })}
                        className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white outline-none focus:border-blue-500"
                      >
                        <option value="professional">Professional Chartered Consultant</option>
                        <option value="formal_legal">Formal Legal & Statutory Counsel</option>
                        <option value="friendly">Friendly & Guided Tax Assistant</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-400 mb-1">Primary Spoken Language</label>
                      <select
                        value={lunaSettings.language}
                        onChange={(e) => setLunaSettings({ ...lunaSettings, language: e.target.value })}
                        className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white outline-none focus:border-blue-500"
                      >
                        <option value="en">English (Official Statutory Terminology)</option>
                        <option value="si">Sinhala (Fluent Sinhala + Financial Terms)</option>
                        <option value="mixed">Bilingual / Mixed (Sinhala & English)</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-400 mb-1">
                      Firm-Specific Custom Instructions & Compliance Policies
                    </label>
                    <p className="text-[11px] text-slate-500 mb-2">
                      Injected directly into Luna's system prompt when clients fill forms for your firm.
                    </p>
                    <textarea
                      rows={5}
                      value={lunaSettings.customInstructions}
                      onChange={(e) => setLunaSettings({ ...lunaSettings, customInstructions: e.target.value })}
                      placeholder="e.g. Ensure all VAT numbers are verified. Ask clients to upload their T-10 certificate before submitting employment income..."
                      className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-4 text-xs text-white font-mono placeholder-slate-600 outline-none focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-800 flex justify-end">
                  <button
                    onClick={handleSaveSettings}
                    disabled={savingSettings}
                    className="px-6 py-3 rounded-2xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs flex items-center gap-2 shadow-lg shadow-blue-600/30 transition-all disabled:opacity-50"
                  >
                    {savingSettings ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles size={14} />}
                    Save Luna AI Studio Settings
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════════
            TAB 6: BRANDING & SETTINGS
        ════════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'branding' && (
          <div className="space-y-6 animate-fade-in max-w-3xl mx-auto">
            <div>
              <h2 className="text-xl font-bold text-white">Firm Profile & White-Label Settings</h2>
              <p className="text-xs text-slate-400">Configure your firm branding, contact info, and client portal appearance</p>
            </div>

            <div className="bg-slate-900/60 backdrop-blur-md border border-slate-800 rounded-3xl p-6 sm:p-8 space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-400 mb-1">Firm Legal Name</label>
                  <input
                    type="text"
                    value={brandingSettings.name}
                    onChange={(e) => setBrandingSettings({ ...brandingSettings, name: e.target.value })}
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-xs text-white outline-none focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-400 mb-1">Custom Portal Title</label>
                  <input
                    type="text"
                    value={brandingSettings.customTitle}
                    onChange={(e) => setBrandingSettings({ ...brandingSettings, customTitle: e.target.value })}
                    placeholder="e.g. Apex Corporate Advisory Client Portal"
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-xs text-white outline-none focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-400 mb-1">Contact Phone</label>
                  <input
                    type="text"
                    value={brandingSettings.phone}
                    onChange={(e) => setBrandingSettings({ ...brandingSettings, phone: e.target.value })}
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-xs text-white outline-none focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-400 mb-1">Firm Logo URL</label>
                  <input
                    type="text"
                    value={brandingSettings.logoUrl}
                    onChange={(e) => setBrandingSettings({ ...brandingSettings, logoUrl: e.target.value })}
                    placeholder="https://example.com/logo.png"
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-xs text-white outline-none focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-400 mb-1">Official Office Address</label>
                <textarea
                  rows={2}
                  value={brandingSettings.address}
                  onChange={(e) => setBrandingSettings({ ...brandingSettings, address: e.target.value })}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-xs text-white outline-none focus:border-blue-500"
                />
              </div>

              <div className="pt-4 border-t border-slate-800 flex justify-end">
                <button
                  onClick={handleSaveSettings}
                  disabled={savingSettings}
                  className="px-6 py-3 rounded-2xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs flex items-center gap-2 shadow-lg shadow-blue-600/30 transition-all disabled:opacity-50"
                >
                  {savingSettings ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check size={14} />}
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* ════════════════════════════════════════════════════════════════════════
          MODAL: ADD CLIENT USER
      ════════════════════════════════════════════════════════════════════════ */}
      {isAddClientModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl max-w-lg w-full p-6 sm:p-8 space-y-6 shadow-2xl relative max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setIsAddClientModalOpen(false)}
              className="absolute top-6 right-6 text-slate-500 hover:text-white"
            >
              <X size={20} />
            </button>

            <div>
              <h3 className="text-lg font-black text-white">Create New Client Account</h3>
              <p className="text-xs text-slate-400">Your client will use these credentials to log in and fill corporate forms with Luna.</p>
            </div>

            <form onSubmit={handleAddClient} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-400 mb-1">Full Legal Name *</label>
                <input
                  type="text"
                  required
                  value={newClient.fullName}
                  onChange={(e) => setNewClient({ ...newClient, fullName: e.target.value })}
                  placeholder="e.g. Kasun Perera"
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-xs text-white outline-none focus:border-blue-500"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-400 mb-1">Email Address *</label>
                  <input
                    type="email"
                    required
                    value={newClient.email}
                    onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                    placeholder="client@example.com"
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-xs text-white outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-400 mb-1">Password *</label>
                  <input
                    type="password"
                    required
                    value={newClient.password}
                    onChange={(e) => setNewClient({ ...newClient, password: e.target.value })}
                    placeholder="••••••••"
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-xs text-white outline-none focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-400 mb-1">Business / Entity Name</label>
                  <input
                    type="text"
                    value={newClient.businessName}
                    onChange={(e) => setNewClient({ ...newClient, businessName: e.target.value })}
                    placeholder="e.g. Perera Holdings PVT LTD"
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-xs text-white outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-400 mb-1">TIN / Tax Number</label>
                  <input
                    type="text"
                    value={newClient.tinNumber}
                    onChange={(e) => setNewClient({ ...newClient, tinNumber: e.target.value })}
                    placeholder="e.g. 109283746"
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-xs text-white outline-none focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-400 mb-1">Phone Number</label>
                <input
                  type="text"
                  value={newClient.phone}
                  onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                  placeholder="+94 77 123 4567"
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-xs text-white outline-none focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-400 mb-1">Internal Notes</label>
                <textarea
                  rows={2}
                  value={newClient.notes}
                  onChange={(e) => setNewClient({ ...newClient, notes: e.target.value })}
                  placeholder="Optional internal remarks about this client..."
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-xs text-white outline-none focus:border-blue-500"
                />
              </div>

              <div className="flex gap-3 pt-4 border-t border-slate-800">
                <button
                  type="submit"
                  disabled={savingClient}
                  className="flex-1 py-3 rounded-2xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs flex items-center justify-center gap-2 shadow-lg shadow-blue-600/30 transition-all disabled:opacity-50"
                >
                  {savingClient ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check size={16} />}
                  Create & Grant Client Access
                </button>
                <button
                  type="button"
                  onClick={() => setIsAddClientModalOpen(false)}
                  className="px-5 py-3 rounded-2xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold transition-all"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════════════
          MODAL: INTERACTIVE FILING REVIEWER
      ════════════════════════════════════════════════════════════════════════ */}
      {selectedFiling && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl max-w-4xl w-full p-6 sm:p-8 space-y-6 shadow-2xl relative max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setSelectedFiling(null)}
              className="absolute top-6 right-6 text-slate-500 hover:text-white"
            >
              <X size={20} />
            </button>

            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-800 pb-4">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-mono uppercase text-blue-400 font-bold tracking-wider">{selectedFiling.serviceType?.replace('_', ' ')}</span>
                  {getStatusBadge(selectedFiling.status)}
                </div>
                <h3 className="text-xl font-black text-white mt-1">{selectedFiling.formTitle}</h3>
                <p className="text-xs text-slate-400">
                  Client: <strong className="text-white">{selectedFiling.client?.fullName}</strong> ({selectedFiling.client?.businessName || selectedFiling.client?.email})
                </p>
              </div>

              {/* Status Update Buttons */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleUpdateFilingStatus(selectedFiling.id, 'approved')}
                  disabled={updatingStatus}
                  className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs flex items-center gap-1.5 shadow-lg shadow-emerald-600/30 transition-all"
                >
                  <CheckCircle2 size={14} /> Approve Filing
                </button>
                <button
                  onClick={() => handleUpdateFilingStatus(selectedFiling.id, 'rejected')}
                  disabled={updatingStatus}
                  className="px-4 py-2 rounded-xl bg-rose-600/20 text-rose-300 border border-rose-500/30 hover:bg-rose-600/30 font-bold text-xs transition-all"
                >
                  Reject
                </button>
              </div>
            </div>

            {/* Form Data Viewer */}
            <div className="space-y-4">
              <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider">Submitted Form Schedule Data</h4>
              <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800 font-mono text-xs text-slate-300 max-h-72 overflow-y-auto space-y-2">
                {Object.entries(selectedFiling.formData || {}).map(([key, val]) => (
                  <div key={key} className="flex flex-col sm:flex-row sm:items-center justify-between py-1.5 border-b border-slate-900">
                    <span className="text-slate-500">{key}:</span>
                    <span className="text-white font-bold text-right break-all">
                      {typeof val === 'object' ? JSON.stringify(val) : String(val)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Supporting OCR Documents */}
            {(selectedFiling.extractedDocuments || []).length > 0 && (
              <div className="space-y-3">
                <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider">Attached Verification Documents</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {(selectedFiling.extractedDocuments || []).map((doc: any, idx: number) => (
                    <div key={idx} className="bg-slate-950/80 p-3 rounded-2xl border border-slate-800 flex items-center justify-between">
                      <div className="flex items-center gap-2 overflow-hidden">
                        <FileCheck size={16} className="text-blue-400 shrink-0" />
                        <span className="text-xs font-mono text-slate-300 truncate">{doc.originalName || doc.filename || 'Document'}</span>
                      </div>
                      {doc.url && (
                        <a
                          href={doc.url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-blue-400 hover:underline shrink-0 ml-2"
                        >
                          View
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Reviewer Notes Editor */}
            <div className="space-y-2">
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider">Reviewer Notes & Feedback to Client</label>
              <textarea
                rows={3}
                value={reviewerNotes}
                onChange={(e) => setReviewerNotes(e.target.value)}
                placeholder="Add audit notes, IRD acknowledgment codes, or revision requests..."
                className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-3 text-xs text-white outline-none focus:border-blue-500"
              />
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-800">
              <button
                onClick={() => setSelectedFiling(null)}
                className="px-5 py-2.5 rounded-2xl bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold text-xs"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
